import { IkeUserRole, type UserProfileJSONObject } from '@ikegps/core-api';
import { IkeFetchableMany, IkeRest, type Log } from '@ikegps/core-shared';
import { computed, reaction, makeObservable } from 'mobx';
import { type IkeClientService, Services } from './index';

export interface DepartmentUserProfile extends UserProfileJSONObject {
	role: IkeUserRole;
	userLevel?: IkeUserRole;
}

export class UserProfileService implements IkeClientService {
	__priority = 100;

	private userProfileFetchable: IkeFetchableMany<DepartmentUserProfile>;

	constructor(protected log: Log) {
		makeObservable(this);
	}

	init() {
		this.userProfileFetchable = new IkeFetchableMany(this.log, IkeRest.createStore(), this.fetchUserProfiles);
		reaction(
			() => Services.Department.current,
			() => {
				if (!Services.Route.profilesLoaded) {
					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					this.userProfileFetchable.fetch().then(() => {
						Services.Route.profilesLoaded = true;
					});
				}
				this.userProfileFetchable.reset();
			}
		);
	}

	@computed get list(): DepartmentUserProfile[] | null {
		return this.userProfileFetchable.value;
	}

	get(userId: string): DepartmentUserProfile | null {
		return this.userProfileFetchable.find('objectId', userId);
	}

	refresh() {
		// eslint-disable-next-line @typescript-eslint/no-floating-promises
		this.userProfileFetchable.refetch();
	}

	isDepartmentAdmin(userProfile: DepartmentUserProfile | null): boolean {
		return userProfile != null && userProfile.role === IkeUserRole.ROOT;
	}

	isDepartmentEditor(userProfile: DepartmentUserProfile | null): boolean {
		return userProfile != null && userProfile.userLevel === IkeUserRole.EDITOR;
	}

	isDepartmentViewer(userProfile: DepartmentUserProfile | null): boolean {
		return userProfile != null && userProfile.userLevel === IkeUserRole.VIEWER;
	}

	isDepartmentDownloader(userProfile: DepartmentUserProfile | null): boolean {
		return userProfile != null && userProfile.userLevel === IkeUserRole.DOWNLOADER;
	}

	isDepartmentDataAdmin(userProfile: DepartmentUserProfile | null): boolean {
		return userProfile != null && userProfile.userLevel === IkeUserRole.DATA_ADMIN;
	}

	isOneOf(userProfile: DepartmentUserProfile | null, userRoles: IkeUserRole[]) {
		for (let userRole of userRoles) {
			if (userProfile != null && userProfile.userLevel === userRole) {
				return true;
			}
		}
		return false;
	}

	isReadOnly(userProfile: DepartmentUserProfile | null): boolean {
		return this.isOneOf(userProfile, [IkeUserRole.VIEWER, IkeUserRole.DOWNLOADER]);
	}

	canExportReports(userProfile: DepartmentUserProfile | null): boolean {
		if (this.isDepartmentViewer(userProfile) || this.isDepartmentEditor(userProfile)) {
			return false;
		}

		return true;
	}

	canImportCollections(userProfile: DepartmentUserProfile | null): boolean {
		if (
			this.isDepartmentDownloader(userProfile) ||
			this.isDepartmentViewer(userProfile) ||
			this.isDepartmentEditor(userProfile)
		) {
			return false;
		}

		return true;
	}

	canEditForms(userProfile: DepartmentUserProfile | null): boolean {
		if (
			this.isDepartmentDownloader(userProfile) ||
			this.isDepartmentViewer(userProfile) ||
			this.isDepartmentEditor(userProfile)
		) {
			return false;
		}

		return true;
	}

	private async fetchUserProfiles(): Promise<DepartmentUserProfile[]> {
		const result: DepartmentUserProfile[] = [];
		const rest = Services.Department.rest;
		if (rest) {
			const departmentUsers = (await rest.Department.User.list(null)).results;
			const userProfiles = await rest
				.query(rest.Objects.UserProfile)
				.in(
					'objectId',
					departmentUsers.map((depUsr) => depUsr.userId)
				)
				.find();

			const userProfileMap: { [userId: string]: UserProfileJSONObject } = {};
			for (const userProfile of userProfiles) {
				userProfileMap[userProfile.objectId] = userProfile;
			}

			for (let i = 0; i < departmentUsers.length; i++) {
				const userProfile = userProfileMap[departmentUsers[i].userId];
				if (userProfile == null) {
					continue;
				}

				result.push({
					role: departmentUsers[i].role,
					userLevel: departmentUsers[i].userLevel,
					...userProfile,
				});
			}
		}
		return result;
	}
}
